import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SendIcon from '@mui/icons-material/Send';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { getGoogleAuthUrl, loginUser } from '../api/services';
import { useAuthentication, useNotifications } from '../store/AppContext';
import { DESIGN_TOKENS } from '../styles/theme';
import { SectionHeader, ErrorDisplay, GoogleAuthButton } from '../components/common';
import { TRANSPORT_FACTS } from '../utils/constants';
import { hasNonEmptyValue, isValidEmail, isValidPassword, isValidPolishPhoneNumber } from '../utils';
import { rememberGoogleAuthIntent } from './AuthCallbackPage';

// ============================================================
// COMPANIES HUB PAGE
// ============================================================

const COMPANY_CATEGORY_TILES = [
  {
    icon: <LocalShippingIcon sx={{ fontSize: 44 }} />,
    title: 'Firmy transportowe',
    description: 'Przewoźnicy i firmy spedycyjne działające w transporcie ciężkim i ponadnormatywnym.',
    path: '/companies/transport-companies',
    isAvailable: true,
    count: '2 400+',
  },
  {
    icon: <PeopleIcon sx={{ fontSize: 44 }} />,
    title: 'Piloci transportu',
    description: 'Certyfikowani piloci pojazdów ponadnormatywnych z uprawnieniami BF3, BF4 i HIPO.',
    path: '/companies/pilots',
    isAvailable: true,
    count: '8 900+',
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 44 }} />,
    title: 'Firmy spedycyjne',
    description: 'Pośrednicy i agenci spedycyjni — dostępne wkrótce.',
    path: '/companies/spedition-companies',
    isAvailable: false,
    count: null,
  },
  {
    icon: <DirectionsCarIcon sx={{ fontSize: 44 }} />,
    title: 'Kierowcy',
    description: 'Baza kierowców zawodowych — dostępna wkrótce.',
    path: '/companies/drivers',
    isAvailable: false,
    count: null,
  },
];

export function CompaniesHubPage() {
  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          py: 7,
        }}
      >
        <Container maxWidth="xl">
          <SectionHeader
            title="Katalog firm i specjalistów"
            subtitle="Przeglądaj bazy firm transportowych, spedycyjnych, pilotów i kierowców w jednym miejscu."
          />
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Grid container spacing={3}>
          {COMPANY_CATEGORY_TILES.map((categoryTile) => (
            <Grid item xs={12} sm={6} key={categoryTile.title}>
              <Card
                component={categoryTile.isAvailable ? RouterLink : Box}
                to={categoryTile.isAvailable ? categoryTile.path : undefined}
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  cursor: categoryTile.isAvailable ? 'pointer' : 'default',
                  opacity: categoryTile.isAvailable ? 1 : 0.55,
                  display: 'flex',
                  alignItems: 'stretch',
                  '&:hover': categoryTile.isAvailable
                    ? { transform: 'translateY(-3px)', boxShadow: DESIGN_TOKENS.shadows.cardHover, textDecoration: 'none', '& .tile-arrow': { transform: 'translateX(4px)' } }
                    : {},
                }}
              >
                <CardContent sx={{ p: 4, display: 'flex', alignItems: 'flex-start', gap: 3, width: '100%' }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 80,
                      height: 80,
                      borderRadius: '20px',
                      backgroundColor: categoryTile.isAvailable
                        ? DESIGN_TOKENS.colors.primaryLighter
                        : DESIGN_TOKENS.colors.background,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: categoryTile.isAvailable ? DESIGN_TOKENS.colors.primary : DESIGN_TOKENS.colors.textDisabled,
                    }}
                  >
                    {categoryTile.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.75 }}>
                        {categoryTile.title}
                      </Typography>
                      {!categoryTile.isAvailable && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: DESIGN_TOKENS.colors.accentLight,
                            color: DESIGN_TOKENS.colors.accentDark,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 8,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          Wkrótce
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                      {categoryTile.description}
                    </Typography>
                    {categoryTile.count && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={700} color="primary">
                          {categoryTile.count} zarejestrowanych
                        </Typography>
                        <ArrowForwardIcon
                          className="tile-arrow"
                          sx={{ color: DESIGN_TOKENS.colors.primary, transition: 'transform 0.2s ease', fontSize: 20 }}
                        />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ============================================================
// LOGIN PAGE
// ============================================================

export function LoginPage() {
  const navigate = useNavigate();
  const { loginUser: setUserLoggedIn, isAuthenticated } = useAuthentication();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/account');
    }
  }, [isAuthenticated, navigate]);

  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasNonEmptyValue(emailOrUsername) || !hasNonEmptyValue(password)) {
      setLoginError('Wypełnij wszystkie pola.');
      return;
    }

    if (!isValidPassword(password)) {
      setLoginError('Hasło musi mieć minimum 8 znaków, jedną cyfrę, jedną wielką literę, jedną małą literę i jeden znak specjalny.');
      return;
    }

    setIsSubmitting(true);
    setLoginError(null);

    try {
      const authResponse = await loginUser({
        identifier: emailOrUsername.trim(),
        password,
      });
      setUserLoggedIn(authResponse.user, authResponse.jwt);
      navigate('/account');
    } catch (error) {
      setLoginError('Nieprawidłowy login lub hasło. Spróbuj ponownie.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    rememberGoogleAuthIntent('login');
    window.location.assign(getGoogleAuthUrl());
  };

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
        py: 6,
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 440 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h2" gutterBottom>
            Zaloguj się
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Witaj ponownie w QPRO Connect
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 3, sm: 4 } }}>
          <CardContent sx={{ p: 0 }}>
            <Box component="form" onSubmit={handleLoginSubmit} noValidate>
              {loginError && (
                <Box sx={{ mb: 2.5 }}>
                  <ErrorDisplay message={loginError} />
                </Box>
              )}
              <TextField
                fullWidth
                label="E-mail lub nazwa użytkownika"
                type="text"
                value={emailOrUsername}
                onChange={(event) => setEmailOrUsername(event.target.value)}
                autoComplete="username"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Hasło"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{ py: 1.5 }}
              >
                {isSubmitting ? 'Logowanie...' : 'Zaloguj się'}
              </Button>
              <Divider sx={{ my: 2.5 }} />
              <GoogleAuthButton
                label="Zaloguj się przez Google"
                onClick={handleGoogleLogin}
                disabled={isSubmitting}
              />
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="body2" align="center" color="text.secondary">
                Nie masz konta?{' '}
                <Typography
                  component={RouterLink}
                  to="/register"
                  variant="body2"
                  sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Zarejestruj się
                </Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

// ============================================================
// CONTACT PAGE
// ============================================================

const CONTACT_EMAIL = 'kontakt@qproconnect.com';

export function ContactPage() {
  const { showNotification } = useNotifications();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleFieldChange = (fieldName: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [fieldName]: event.target.value }));
  };

  const handleContactSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!hasNonEmptyValue(formData.name) || !hasNonEmptyValue(formData.email) || !hasNonEmptyValue(formData.message)) {
      setFormError('Uzupełnij imię i nazwisko, adres e-mail oraz treść wiadomości.');
      return;
    }

    if (!isValidEmail(formData.email.trim())) {
      setFormError('Podaj poprawny adres e-mail.');
      return;
    }

    if (hasNonEmptyValue(formData.phone) && !isValidPolishPhoneNumber(formData.phone.trim())) {
      setFormError('Podaj poprawny numer telefonu albo pozostaw to pole puste.');
      return;
    }

    setFormError(null);

    const subject = formData.subject.trim() || 'Wiadomość z formularza kontaktowego QPRO Connect';
    const body = [
      `Imię i nazwisko: ${formData.name.trim()}`,
      `E-mail: ${formData.email.trim()}`,
      formData.phone.trim() ? `Telefon: ${formData.phone.trim()}` : null,
      '',
      formData.message.trim(),
    ].filter(Boolean).join('\n');

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    showNotification('Otwieramy klienta pocztowego z przygotowaną wiadomością.', 'success');
  };

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '70vh' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h1" sx={{ fontSize: { xs: '2.15rem', md: '3rem' }, mb: 2 }}>
                Kontakt
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, lineHeight: 1.75 }}>
                Napisz do nas w sprawie współpracy, konta, ogłoszeń lub widoczności firmy w katalogu.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ border: `1px solid ${DESIGN_TOKENS.colors.border}`, boxShadow: DESIGN_TOKENS.shadows.card }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Bezpośredni kontakt
                  </Typography>
                  <Typography
                    component="a"
                    href={`mailto:${CONTACT_EMAIL}`}
                    variant="body1"
                    sx={{ color: DESIGN_TOKENS.colors.primary, fontWeight: 700, textDecoration: 'none' }}
                  >
                    {CONTACT_EMAIL}
                  </Typography>
                  <Divider sx={{ my: 2.5 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Formularz przygotuje wiadomość w Twoim programie pocztowym wraz z tematem i danymi kontaktowymi.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Card sx={{ border: `1px solid ${DESIGN_TOKENS.colors.border}`, boxShadow: DESIGN_TOKENS.shadows.card }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box component="form" onSubmit={handleContactSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    Wyślij wiadomość
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Podaj najważniejsze informacje, a wiadomość trafi do nas w uporządkowanej formie.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                  {formError && (
                    <Box sx={{ mb: 2 }}>
                      <ErrorDisplay message={formError} />
                    </Box>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Imię i nazwisko"
                        value={formData.name}
                        onChange={handleFieldChange('name')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="E-mail"
                        type="email"
                        value={formData.email}
                        onChange={handleFieldChange('email')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Telefon"
                        value={formData.phone}
                        onChange={handleFieldChange('phone')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneOutlinedIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Temat"
                        value={formData.subject}
                        onChange={handleFieldChange('subject')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MessageOutlinedIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        minRows={6}
                        label="Wiadomość"
                        value={formData.message}
                        onChange={handleFieldChange('message')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" size="large" endIcon={<SendIcon />} sx={{ px: 4 }}>
                        Wyślij wiadomość
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

// ============================================================
// 404 NOT FOUND PAGE
// ============================================================

export function NotFoundPage() {
  const navigate = useNavigate();
  const randomFact = TRANSPORT_FACTS[Math.floor(Math.random() * TRANSPORT_FACTS.length)];

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}60, ${DESIGN_TOKENS.colors.surface})`,
        py: 8,
        px: 2,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
        {/* 404 display */}
        <Box
          sx={{
            fontSize: { xs: '8rem', md: '12rem' },
            fontWeight: 900,
            lineHeight: 0.9,
            fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLight}, ${DESIGN_TOKENS.colors.primary})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            letterSpacing: '-0.04em',
            userSelect: 'none',
          }}
        >
          404
        </Box>

        <SentimentDissatisfiedIcon sx={{ fontSize: 56, color: DESIGN_TOKENS.colors.primaryLight, mb: 2 }} />

        <Typography variant="h3" sx={{ mb: 1.5 }}>
          Strona nie istnieje
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Wygląda na to, że ładunek dotarł nie pod ten adres. Może ktoś zmienił trasę?
        </Typography>

        <Box
          sx={{
            p: 3,
            backgroundColor: DESIGN_TOKENS.colors.surface,
            borderRadius: DESIGN_TOKENS.borderRadius.card,
            border: `1px solid ${DESIGN_TOKENS.colors.border}`,
            mb: 4,
            textAlign: 'left',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, mb: 1, display: 'block' }}>
            🚛 Ciekawostka transportowa
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
            {randomFact}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            size="large"
            sx={{ px: 4 }}
          >
            Strona główna
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            size="large"
            sx={{ px: 4 }}
          >
            Wróć
          </Button>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <SportsEsportsIcon color="primary" /> Strefa relaksu dla kierowcy
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
            <Button
              component={RouterLink}
              to="/dinozaur"
              variant="text"
              size="small"
              startIcon={<LocalShippingIcon />}
            >
              Dino Trucker
            </Button>
            <Button
              component={RouterLink}
              to="/sokoban"
              variant="text"
              size="small"
              startIcon={<SportsEsportsIcon />}
            >
              Sokoban Master
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
